import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Briefcase, AlertCircle, UserPlus, ArrowLeft, CheckCircle, Mail, KeyRound, Eye, EyeOff } from 'lucide-react';
import { seedData, registerPublic, forgotPassword, resetPassword } from '../services/api';

const PasswordInput = ({ id, value, onChange, placeholder = '••••••••', required = true, minLength, testId, autoComplete }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        id={id}
        type={show ? 'text' : 'password'}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        minLength={minLength}
        autoComplete={autoComplete}
        data-testid={testId}
        className="border-slate-300 pr-10"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        tabIndex={-1}
        aria-label={show ? 'Ocultar senha' : 'Mostrar senha'}
        className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-600"
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
};

const LoginPage = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);

  // Registration state
  const [showRegister, setShowRegister] = useState(false);
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regArea, setRegArea] = useState('Geral');
  const [regBirthday, setRegBirthday] = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const [regSuccess, setRegSuccess] = useState(false);
  const [regError, setRegError] = useState('');

  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState('');

  // Reset password state (triggered by ?reset_token=xxx in URL)
  const [resetToken, setResetToken] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  const [resetError, setResetError] = useState('');

  // Show login password
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('reset_token');
    if (token) {
      setResetToken(token);
    }
  }, []);

  const clearResetTokenFromUrl = () => {
    if (window.location.search) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegError('');
    setRegLoading(true);

    try {
      await registerPublic({
        email: regEmail,
        password: regPassword,
        name: regName,
        area: regArea,
        birthday: regBirthday || null
      });
      setRegSuccess(true);
    } catch (err) {
      setRegError(err.response?.data?.detail || 'Erro ao fazer cadastro');
    } finally {
      setRegLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotLoading(true);

    try {
      await forgotPassword(forgotEmail);
      setForgotSent(true);
    } catch (err) {
      setForgotError(err.response?.data?.detail || 'Erro ao processar solicitação');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setResetError('');

    if (newPassword.length < 6) {
      setResetError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setResetError('As senhas não coincidem.');
      return;
    }

    setResetLoading(true);
    try {
      await resetPassword(resetToken, newPassword);
      setResetDone(true);
      clearResetTokenFromUrl();
    } catch (err) {
      setResetError(err.response?.data?.detail || 'Erro ao redefinir senha');
    } finally {
      setResetLoading(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const result = await seedData();
      if (result.admin_email) {
        setEmail(result.admin_email);
        setPassword(result.admin_password);
        alert(`Dados criados!\nEmail: ${result.admin_email}\nSenha: ${result.admin_password}`);
      } else {
        alert(result.message);
      }
    } catch (err) {
      alert('Erro ao criar dados iniciais');
    } finally {
      setSeeding(false);
    }
  };

  // Reset password success screen
  if (resetDone) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50">
        <Card className="w-full max-w-md border-slate-200 shadow-sm">
          <CardContent className="pt-8 text-center">
            <div className="p-4 bg-green-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-heading font-bold text-slate-900 mb-2">Senha Redefinida!</h2>
            <p className="text-slate-500 mb-6">
              Sua nova senha foi salva com sucesso. Você já pode fazer login.
            </p>
            <Button
              onClick={() => {
                setResetToken(null);
                setResetDone(false);
                setNewPassword('');
                setConfirmPassword('');
              }}
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              data-testid="reset-done-back"
            >
              Voltar para Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Reset password form (URL has ?reset_token=xxx)
  if (resetToken) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-indigo-600 rounded-lg">
              <KeyRound className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-bold text-slate-900">Redefinir Senha</h1>
              <p className="text-sm text-slate-500">Crie uma nova senha para acessar sua conta</p>
            </div>
          </div>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading">Nova Senha</CardTitle>
              <CardDescription>Escolha uma senha de pelo menos 6 caracteres</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleReset} className="space-y-4">
                {resetError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-center gap-2 text-sm" data-testid="reset-error">
                    <AlertCircle className="h-4 w-4" />
                    {resetError}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="new-password">Nova Senha</Label>
                  <PasswordInput
                    id="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    testId="reset-new-password"
                    autoComplete="new-password"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirmar Senha</Label>
                  <PasswordInput
                    id="confirm-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    testId="reset-confirm-password"
                    autoComplete="new-password"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  disabled={resetLoading}
                  data-testid="reset-submit"
                >
                  {resetLoading ? 'Salvando...' : 'Redefinir Senha'}
                </Button>

                <button
                  type="button"
                  onClick={() => {
                    setResetToken(null);
                    setResetError('');
                    setNewPassword('');
                    setConfirmPassword('');
                    clearResetTokenFromUrl();
                  }}
                  className="w-full text-sm text-slate-500 hover:text-slate-700 mt-2"
                >
                  Cancelar e voltar para login
                </button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Registration success screen
  if (regSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50">
        <Card className="w-full max-w-md border-slate-200 shadow-sm">
          <CardContent className="pt-8 text-center">
            <div className="p-4 bg-green-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-heading font-bold text-slate-900 mb-2">Cadastro Enviado!</h2>
            <p className="text-slate-500 mb-6">
              Seu cadastro foi recebido e está aguardando aprovação do administrador.
              Você receberá acesso assim que for aprovado.
            </p>
            <Button
              onClick={() => {
                setShowRegister(false);
                setRegSuccess(false);
                setRegName('');
                setRegEmail('');
                setRegPassword('');
                setRegArea('Geral');
                setRegBirthday('');
              }}
              className="w-full bg-indigo-600 hover:bg-indigo-700"
            >
              Voltar para Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Forgot password success screen
  if (forgotSent) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50">
        <Card className="w-full max-w-md border-slate-200 shadow-sm">
          <CardContent className="pt-8 text-center">
            <div className="p-4 bg-indigo-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <Mail className="h-8 w-8 text-indigo-600" />
            </div>
            <h2 className="text-xl font-heading font-bold text-slate-900 mb-2">Verifique seu email</h2>
            <p className="text-slate-500 mb-6">
              Se o email <span className="font-semibold text-slate-700">{forgotEmail}</span> estiver cadastrado, você receberá um link para redefinir sua senha em alguns minutos.
            </p>
            <p className="text-xs text-slate-400 mb-6">
              Não esqueça de conferir a caixa de spam. O link é válido por 1 hora.
            </p>
            <Button
              onClick={() => {
                setShowForgot(false);
                setForgotSent(false);
                setForgotEmail('');
                setForgotError('');
              }}
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              data-testid="forgot-sent-back"
            >
              Voltar para Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Forgot password form
  if (showForgot) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <button
            onClick={() => {
              setShowForgot(false);
              setForgotError('');
              setForgotEmail('');
            }}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar para login
          </button>

          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-indigo-600 rounded-lg">
              <Mail className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-bold text-slate-900">Esqueci minha senha</h1>
              <p className="text-sm text-slate-500">Enviaremos um link para redefinir sua senha</p>
            </div>
          </div>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading">Recuperar acesso</CardTitle>
              <CardDescription>Informe o email cadastrado e enviaremos um link de redefinição</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgot} className="space-y-4">
                {forgotError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-center gap-2 text-sm" data-testid="forgot-error">
                    <AlertCircle className="h-4 w-4" />
                    {forgotError}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="forgot-email">Email cadastrado</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="seu@email.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    autoComplete="email"
                    data-testid="forgot-email"
                    className="border-slate-300"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  disabled={forgotLoading}
                  data-testid="forgot-submit"
                >
                  {forgotLoading ? 'Enviando...' : 'Enviar link de redefinição'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Registration form
  if (showRegister) {
    return (
      <div className="min-h-screen flex">
        {/* Left side - Image */}
        <div className="hidden lg:flex lg:w-1/2 relative">
          <img
            src="https://images.unsplash.com/photo-1589936009537-bda4341d578f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
            alt="Office"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/60" />
          <div className="relative z-10 flex flex-col justify-end p-12 text-white">
            <h2 className="text-3xl font-heading font-bold mb-4">Junte-se a Nós</h2>
            <p className="text-slate-300 text-lg">
              Cadastre-se para ter acesso ao portal de parceiros e gerenciar sua disponibilidade.
            </p>
          </div>
        </div>

        {/* Right side - Register Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
          <div className="w-full max-w-md">
            <button
              onClick={() => setShowRegister(false)}
              className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar para login
            </button>

            <div className="flex items-center gap-3 mb-8">
              <div className="p-3 bg-indigo-600 rounded-lg">
                <UserPlus className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-heading font-bold text-slate-900">Criar Conta</h1>
                <p className="text-sm text-slate-500">Preencha seus dados para se cadastrar</p>
              </div>
            </div>

            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="font-heading">Novo Cadastro</CardTitle>
                <CardDescription>Seu acesso será liberado após aprovação do administrador</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegister} className="space-y-4">
                  {regError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-center gap-2 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      {regError}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="reg-name">Nome Completo *</Label>
                    <Input
                      id="reg-name"
                      type="text"
                      placeholder="Seu nome completo"
                      value={regName}
                      onChange={(e) => setRegName(e.target.value)}
                      required
                      data-testid="register-name"
                      className="border-slate-300"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reg-email">Email *</Label>
                    <Input
                      id="reg-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={regEmail}
                      onChange={(e) => setRegEmail(e.target.value)}
                      required
                      data-testid="register-email"
                      className="border-slate-300"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reg-password">Senha *</Label>
                    <PasswordInput
                      id="reg-password"
                      value={regPassword}
                      onChange={(e) => setRegPassword(e.target.value)}
                      required
                      minLength={6}
                      testId="register-password"
                      autoComplete="new-password"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="reg-area">Área</Label>
                      <Input
                        id="reg-area"
                        type="text"
                        placeholder="Ex: TI, RH, Vendas"
                        value={regArea}
                        onChange={(e) => setRegArea(e.target.value)}
                        data-testid="register-area"
                        className="border-slate-300"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="reg-birthday">Aniversário</Label>
                      <Input
                        id="reg-birthday"
                        type="date"
                        value={regBirthday}
                        onChange={(e) => setRegBirthday(e.target.value)}
                        data-testid="register-birthday"
                        className="border-slate-300"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                    disabled={regLoading}
                    data-testid="register-submit"
                  >
                    {regLoading ? 'Cadastrando...' : 'Solicitar Cadastro'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1589936009537-bda4341d578f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Office"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/60" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h2 className="text-3xl font-heading font-bold mb-4">Portal de Parceiros</h2>
          <p className="text-slate-300 text-lg">
            Cronograma de disponibilidade e alinhamento operacional com parceiros de forma simples e eficiente.
          </p>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-indigo-600 rounded-lg">
              <Briefcase className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-bold text-slate-900">Portal de Parceiros</h1>
              <p className="text-sm text-slate-500">Cronograma de Disponibilidade</p>
            </div>
          </div>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading">Bem-vindo de volta</CardTitle>
              <CardDescription>Entre com suas credenciais para acessar o sistema</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-center gap-2 text-sm" data-testid="login-error">
                    <AlertCircle className="h-4 w-4" />
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    data-testid="login-email"
                    className="border-slate-300"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Senha</Label>
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgot(true);
                        setForgotEmail(email);
                      }}
                      className="text-xs text-indigo-600 hover:text-indigo-700 hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Esqueci minha senha
                    </button>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showLoginPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      data-testid="login-password"
                      className="border-slate-300 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword((s) => !s)}
                      tabIndex={-1}
                      aria-label={showLoginPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-600"
                      data-testid="toggle-login-password"
                    >
                      {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? 'Entrando...' : 'Entrar'}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-slate-200">
                <Button
                  variant="outline"
                  className="w-full border-slate-300"
                  onClick={() => setShowRegister(true)}
                  data-testid="register-button"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Criar Nova Conta
                </Button>
              </div>
            </CardContent>
          </Card>

          <p className="text-xs text-slate-400 text-center mt-6">
            WeConnect360 - Portal de Parceiros
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
